import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { Config } from '@luggagehero/shared/environment';
import { BookableStorageLocation, Booking } from '@luggagehero/shared/interfaces';
import { SharedBookingService } from '@luggagehero/shared/services/bookings';
import { SharedUiAccessCodeComponent } from '@luggagehero/shared/ui';
import { TranslatePipe } from '@luggagehero/shared/ui-pipes';

interface UpdateParams {
  accessCode?: boolean;
  accessText?: boolean;
}

type Size = 'small' | 'medium' | 'large';

@Component({
  selector: 'lh-shared-feature-bookings-storage-room-access-info',
  standalone: true,
  imports: [CommonModule, SharedUiAccessCodeComponent, TranslatePipe],
  templateUrl: './shared-feature-bookings-storage-room-access-info.component.html',
  styleUrl: './shared-feature-bookings-storage-room-access-info.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SharedFeatureBookingsStorageRoomAccessInfoComponent implements OnInit {
  private _booking: Booking | undefined;
  private _storageLocation: BookableStorageLocation | undefined;
  private _darkMode = false;
  private _size: Size = 'large';
  private _accessCode: string | undefined;
  private _accessText: string | undefined;
  private _hideAccessText = false;
  private _hideAccessCode = false;
  private _hideAccessLabel = false;
  private _isInitialized = false;

  constructor(
    private bookingService: SharedBookingService,
    private cd: ChangeDetectorRef,
  ) {}

  public get booking(): Booking | undefined {
    return this._booking;
  }
  @Input() public set booking(value: Booking) {
    if (!Config.isProduction) {
      console.log(`SharedFeatureBookingsStorageRoomAccessInfoComponent: set booking (${value?._id})`, value);
    }
    this._booking = value;

    if (this.isInitialized) {
      void this.update({ accessCode: true });
    }
  }

  public get storageLocation(): BookableStorageLocation | undefined {
    return this._storageLocation;
  }
  @Input() public set storageLocation(value: BookableStorageLocation) {
    if (!Config.isProduction) {
      console.log(`SharedFeatureBookingsStorageRoomAccessInfoComponent: set storageLocation (${value?._id})`, value);
    }
    this._storageLocation = value;

    if (this.isInitialized) {
      void this.update({ accessCode: true, accessText: true });
    }
  }

  public get darkMode(): boolean {
    return this._darkMode;
  }
  @Input() public set darkMode(value: boolean) {
    this._darkMode = value;
    this.cd.markForCheck();
  }

  public get size(): Size {
    return this._size;
  }
  @Input() public set size(value: Size) {
    if (this._size !== value) {
      this._size = value;
      this.cd.markForCheck();
    }
  }

  public get hideAccessText(): boolean {
    return this._hideAccessText;
  }
  @Input() public set hideAccessText(value: boolean) {
    if (this._hideAccessText !== value) {
      this._hideAccessText = value;
      this.cd.markForCheck();
    }
  }

  public get hideAccessCode(): boolean {
    return this._hideAccessCode;
  }
  @Input() public set hideAccessCode(value: boolean) {
    if (this._hideAccessCode !== value) {
      this._hideAccessCode = value;
      this.cd.markForCheck();
    }
  }

  public get hideAccessLabel(): boolean {
    return this._hideAccessLabel;
  }
  @Input() public set hideAccessLabel(value: boolean) {
    if (this._hideAccessLabel !== value) {
      this._hideAccessLabel = value;
      this.cd.markForCheck();
    }
  }

  public get accessCode(): string | undefined {
    return this._accessCode;
  }

  public get accessText(): string | undefined {
    return this._accessText;
  }

  get showAccessText(): boolean {
    return !!this.accessText && !this.hideAccessText;
  }

  get showAccessCode(): boolean {
    return !!this.accessCode && !this.hideAccessCode;
  }

  get showAccessLabel(): boolean {
    return (this.showAccessCode || this.showAccessText) && !this.hideAccessLabel;
  }

  public get isInitialized(): boolean {
    return this._isInitialized;
  }

  public async ngOnInit(): Promise<void> {
    if (!Config.isProduction) {
      console.log(
        `SharedFeatureBookingsStorageRoomAccessInfoComponent: ngOnInit (${this.booking?._id}) (${this.storageLocation?._id})`,
        {
          booking: this.booking,
          storageLocation: this.storageLocation,
        },
      );
    }
    await this.update({ accessCode: true, accessText: true });
  }

  private async update(params: UpdateParams): Promise<void> {
    try {
      if (params.accessCode) {
        this._accessCode = await this.getAccessCode();
      }
      if (params.accessText) {
        this._accessText = this.getAccessText();
      }
    } finally {
      if (!Config.isProduction) {
        console.log(
          `SharedFeatureBookingsStorageRoomAccessInfoComponent: update (${this.accessCode}) (${this.accessText})`,
          {
            booking: this.booking,
            storageLocation: this.storageLocation,
            isInitialized: this.isInitialized,
          },
        );
      }
      this._isInitialized = true;
      this.cd.markForCheck();
    }
  }

  private async getAccessCode(): Promise<string> {
    if (!this.booking || !this.bookingService.isStorageAccessAllowed(this.booking)) {
      return null;
    }

    if (!this.storageLocation || !this.storageLocation.isSelfService) {
      return null;
    }

    if (!this.storageLocation.hasALock) {
      return this.storageLocation.storageRoomAccessCode || null;
    }

    try {
      // don't always go to the service and create a new pin - especially after pickup
      return this.bookingService.getStorageAccessCode(this.booking._id);
    } catch (err) {
      return this.storageLocation.storageRoomAccessBackupCode || null;
    }
  }

  private getAccessText(): string {
    if (!this.storageLocation || !this.storageLocation.isSelfService) {
      return null;
    }

    if (!this.booking) {
      return null;
    }

    if (!this.bookingService.isStorageAccessAllowed(this.booking)) {
      return 'BOOKING_STORAGE_ACCESS_EXPIRED';
    }

    return this.storageLocation.storageRoomAccessText || null;
  }
}
